import { ClipboardCopyIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import copy from "copy-to-clipboard";
import { useState } from "react";

import Button from "@components/button/button";

const MCP_DOCS_URL = "https://topicflow.mintlify.app/mcp-server";

const CopyField = ({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copy(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
          {label}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          aria-label={`Copy ${label}`}
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardCopyIcon className="h-4 w-4" />
          )}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      {multiline ? (
        <pre className="overflow-x-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
          <code>{value}</code>
        </pre>
      ) : (
        <code className="block break-all rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          {value}
        </code>
      )}
    </div>
  );
};

const McpIntegrationSection = () => {
  const mcpUrl = `${window.location.origin}/mcp`;
  const jsonConfig = JSON.stringify(
    { mcpServers: { topicflow: { type: "http", url: mcpUrl } } },
    null,
    2,
  );

  return (
    <section aria-label="AI tools section" className="bg-white py-4">
      <h3 className="text-lg font-medium text-gray-800">AI tools</h3>
      <div className="py-6 border-0 border-b border-gray-200">
        <p>
          Connect Topicflow to your AI assistant with our MCP (Model Context
          Protocol) server. Once connected, your assistant can read and update
          your goals, meetings, feedback, and more — after you approve access.
        </p>
        <CopyField label="Connection URL" value={mcpUrl} />
        <CopyField label="JSON config" value={jsonConfig} multiline />
        <p className="mt-3 text-sm text-gray-400">
          Works with Claude, ChatGPT, Notion, GitHub Copilot, Gemini, and other
          MCP clients.
        </p>
        <div className="mt-4">
          <Button
            rounded
            url={MCP_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            View setup guide
          </Button>
        </div>
      </div>
    </section>
  );
};

export default McpIntegrationSection;
